// src/services/mapiService.js - ИСПРАВЛЕННАЯ ВЕРСИЯ ДЛЯ ПРОКСИ СЕРВЕРА
class MapiService {
  constructor() {
    this.isDevelopment = process.env.NODE_ENV === 'development';
    
    // ✅ ИСПОЛЬЗУЕМ ПРОКСИ СЕРВЕР вместо прямых MAPI endpoints
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';
    console.log('🔧 MapiService baseUrl:', baseUrl);
    
    this.endpoints = {
      geocoding: `${baseUrl}/geocoding`,
      pricing: `${baseUrl}/pricing`,
      maps: baseUrl,
      tiles: `${baseUrl}/tiles`,
      boundaries: `${baseUrl}/geocoding` // boundaries через geocoding прокси
    };

    // ❌ УБИРАЕМ API КЛЮЧИ - они теперь ТОЛЬКО в бэкенде!
    // this.apiKeys = { ... }
    
    this.requestCount = 0;
    this.lastRequestTime = 0;
    this.minInterval = 100;
  }

  // 🔥 НОВАЯ ФУНКЦИЯ: Получение данных для heatmap
  async getHeatmapData(datasetId = 'schools') {
    try {
      console.log('📊 Getting heatmap data for:', datasetId);
      
      // Используем прокси endpoint для heatmap
      const endpoint = `${this.endpoints.maps}/heatmap/${datasetId}`;
      
      const response = await fetch(endpoint);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to fetch heatmap data`);
      }
      
      const data = await response.json();
      console.log('📊 Heatmap data received:', data);
      
      return data;

    } catch (error) {
      console.error('📊 Heatmap data error:', error);
      
      // Fallback to mock data
      return this.generateMockHeatmapData(datasetId);
    }
  }

  // 🎭 MOCK данные для демонстрации
  generateMockHeatmapData(datasetId) {
    console.log('🎭 Generating mock heatmap data for:', datasetId);
    
    const features = [];
    const count = 1000; // Генерируем 1000 точек
    
    // Координаты для разных регионов
    const regions = [
      { name: 'NYC', center: [-73.9857, 40.7484], spread: 0.5 },
      { name: 'LA', center: [-118.2437, 34.0522], spread: 0.8 },
      { name: 'Chicago', center: [-87.6298, 41.8781], spread: 0.6 },
      { name: 'Houston', center: [-95.3698, 29.7604], spread: 0.7 },
      { name: 'Phoenix', center: [-112.0740, 33.4484], spread: 0.9 }
    ];
    
    for (let i = 0; i < count; i++) {
      const region = regions[Math.floor(Math.random() * regions.length)];
      
      // Генерируем координаты вокруг центра региона
      const lat = region.center[1] + (Math.random() - 0.5) * region.spread;
      const lng = region.center[0] + (Math.random() - 0.5) * region.spread;
      
      // Генерируем реалистичные данные школы
      const students = Math.floor(Math.random() * 3000) + 100; // 100-3100 студентов
      const teachers = Math.floor(students / 15) + Math.floor(Math.random() * 10); // примерно 15 студентов на учителя
      
      features.push({
        type: 'Feature',
        properties: {
          id: `school_${i}`,
          name: `${region.name} School ${i % 50 + 1}`,
          students: students,
          teachers: teachers,
          region: region.name,
          grade_span: Math.random() > 0.3 ? 'K-12' : Math.random() > 0.5 ? 'K-8' : '9-12',
          school_type: Math.random() > 0.1 ? 'Regular' : 'Special',
          // Добавляем density для более реалистичного распределения
          density: Math.floor(students / 100) * 100 // группируем по 100
        },
        geometry: {
          type: 'Point',
          coordinates: [lng, lat]
        }
      });
    }
    
    return {
      type: 'FeatureCollection',
      features: features,
      metadata: {
        title: `Mock ${datasetId} Data`,
        description: 'Generated mock data for demonstration',
        total_features: features.length,
        generated_at: new Date().toISOString()
      }
    };
  }

  // 🔧 ДОПОЛНИТЕЛЬНЫЕ ФУНКЦИИ для работы с heatmap данными
  async getDatasetInfo(datasetId) {
    try {
      const response = await this.throttledRequest(`${this.endpoints.maps}/heatmap/${datasetId}/info`);
      return response;
    } catch (error) {
      return {
        id: datasetId,
        name: `Mock ${datasetId} Dataset`,
        description: 'Mock dataset for demonstration',
        features_count: 1000,
        property_fields: ['students', 'teachers', 'region'],
        bbox: [-125, 25, -65, 50] // США
      };
    }
  }

  // 🎯 Получение доступных datasets
  async getAvailableDatasets() {
    try {
      const response = await this.throttledRequest(`${this.endpoints.maps}/heatmap/datasets`);
      return response.datasets || [];
    } catch (error) {
      return [
        { id: 'schools', name: 'US Public Schools', type: 'educational' },
        { id: 'hospitals', name: 'US Hospitals', type: 'medical' },
        { id: 'restaurants', name: 'Restaurants', type: 'commercial' }
      ];
    }
  }

  // ✅ УПРОЩЕНА: Получение токена через прокси
  async generateMapToken() {
    try {
      console.log('🔑 Requesting map token via PROXY...');
      
      const response = await fetch(`${this.endpoints.maps}/maps/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to parse error response' }));
        throw new Error(`HTTP ${response.status}: ${errorData.error || 'Failed to get token'}`);
      }

      const data = await response.json();
      console.log('✅ Token received via PROXY:', data);
      return data.access_token;

    } catch (error) {
      console.error('❌ Map token request via PROXY failed:', error);
      return null;
    }
  }

  async throttledRequest(url, options = {}) {
    const now = Date.now();
    const timeSinceLastRequest = now - this.lastRequestTime;
    
    if (timeSinceLastRequest < this.minInterval) {
      await new Promise(resolve => setTimeout(resolve, this.minInterval - timeSinceLastRequest));
    }

    this.requestCount++;
    this.lastRequestTime = Date.now();

    console.log(`📡 PROXY Request #${this.requestCount}:`, url);
    
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers
    };

    // ❌ УБИРАЕМ логику добавления API ключей - это делает бэкенд прокси!
    
    try {
      const response = await fetch(url, {
        ...options,
        headers
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${response.statusText}. Response: ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`❌ PROXY Request failed:`, error);
      throw error;
    }
  }

  async searchPlaces(query, options = {}) {
    try {
      const { size = 5, lat, lng, include_boundaries = true } = options;
      
      console.log('🔍 Searching places via PROXY:', query);
      
      const searchParams = new URLSearchParams({
        text: query,
        size: size.toString()
      });

      if (lat && lng) {
        searchParams.append('lat', lat.toString());
        searchParams.append('lng', lng.toString());
      }

      if (include_boundaries) {
        searchParams.append('boundary', 'true');
      }

      const data = await this.throttledRequest(
        `${this.endpoints.geocoding}/search?${searchParams}`
      );

      console.log('🔍 PROXY Search results:', data);

      if (data.features && Array.isArray(data.features)) {
        return data.features.map((feature, index) => {
          const place = {
            id: feature.properties?.id || feature.properties?.osm_id || `place_${index}`,
            name: feature.properties?.name || feature.properties?.label || 'Unknown Place',
            country: feature.properties?.country || 'Unknown',
            region: feature.properties?.region || feature.properties?.locality || feature.properties?.state || 'Unknown',
            coordinates: feature.geometry?.coordinates ? [feature.geometry.coordinates[0], feature.geometry.coordinates[1]] : [0, 0],
            confidence: feature.properties?.confidence || 0.5,
            type: this.detectPlaceType(feature.properties)
          };

          // Добавляем границы если они есть
          if (feature.bbox || (feature.geometry && feature.geometry.type !== 'Point')) {
            place.boundaries = feature.geometry;
          }

          return place;
        });
      }

      return [];

    } catch (error) {
      console.error('🔍 PROXY Search error:', error);
      
      return [{
        id: 'error_fallback',
        name: `Search: ${query}`,
        country: 'Demo',
        region: 'Fallback',
        coordinates: [37.6156, 55.7522],
        confidence: 0.5,
        type: 'point'
      }];
    }
  }

  // ✅ НОВАЯ ФУНКЦИЯ: Получение границ по ID места через прокси
  async getPlaceBoundaries(placeId) {
    try {
      console.log('🗺️ Getting boundaries for place ID via PROXY:', placeId);
      
      const data = await this.throttledRequest(
        `${this.endpoints.boundaries}/boundaries/${placeId}`
      );

      console.log('🗺️ PROXY Boundaries result:', data);
      
      if (data.geometry) {
        return data.geometry;
      }
      
      return null;

    } catch (error) {
      console.warn('🗺️ PROXY Boundaries error:', error);
      return null;
    }
  }

  // ✅ НОВАЯ ФУНКЦИЯ: Определение типа места
  detectPlaceType(properties) {
    if (!properties) return 'point';
    
    const layer = properties.layer?.toLowerCase();
    const source = properties.source?.toLowerCase();
    const placetype = properties.placetype?.toLowerCase();
    
    // Приоритет типам из API
    if (placetype) {
      if (placetype.includes('country')) return 'country';
      if (placetype.includes('region') || placetype.includes('state')) return 'region';
      if (placetype.includes('city') || placetype.includes('locality')) return 'city';
      if (placetype.includes('district') || placetype.includes('neighbourhood')) return 'district';
    }
    
    // Анализ по layer
    if (layer) {
      if (layer === 'country') return 'country';
      if (layer === 'region' || layer === 'macroregion') return 'region';
      if (layer === 'locality' || layer === 'localadmin') return 'city';
      if (layer === 'neighbourhood' || layer === 'borough') return 'district';
      if (layer === 'venue' || layer === 'address') return 'venue';
    }
    
    // Анализ по source
    if (source) {
      if (source.includes('admin')) return 'region';
      if (source.includes('city') || source.includes('town')) return 'city';
    }
    
    return 'point';
  }

  async getPrice(lat, lng, resolution = 9) {
    try {
      console.log('💰 Getting price via PROXY:', { lat, lng, resolution });
      
      const data = await this.throttledRequest(
        `${this.endpoints.pricing}/hexagon?lat=${lat}&lng=${lng}&resolution=${resolution}`
      );

      console.log('💰 PROXY Price data received:', data);
      
      if (data.success && data.data) {
        return {
          h3Index: data.data.h3Index,
          h3Resolution: resolution,
          lat: data.data.coordinates?.lat || lat,
          lng: data.data.coordinates?.lng || lng,
          price: data.data.price,
          area: data.data.area,
          metadata: data.data.metadata,
          calculatedAt: data.calculated_at
        };
      }
      
      // Если структура ответа другая, пробуем адаптировать
      return data;

    } catch (error) {
      console.error('💰 PROXY Pricing error:', error);
      throw error;
    }
  }

  async getPriceHistory(lat, lng, period = '1year') {
    try {
      console.log('📈 Getting price history via PROXY:', { lat, lng, period });
      
      const data = await this.throttledRequest(
        `${this.endpoints.pricing}/hexagon/history?lat=${lat}&lng=${lng}&period=${period}`
      );

      console.log('📈 PROXY Price history received:', data);
      return data;

    } catch (error) {
      console.error('📈 PROXY Price history error:', error);
      throw error;
    }
  }

  async reverseGeocode(lat, lng) {
    try {
      console.log('🔄 Reverse geocoding via PROXY:', { lat, lng });
      
      const data = await this.throttledRequest(
        `${this.endpoints.geocoding}/reverse?lat=${lat}&lon=${lng}`
      );

      console.log('🔄 PROXY Reverse geocoding result:', data);
      return data;

    } catch (error) {
      console.error('🔄 PROXY Reverse geocoding error:', error);
      throw error;
    }
  }

  async checkApiStatus() {
    try {
      const response = await this.throttledRequest(`${this.endpoints.maps}/usage`);
      return { status: 'healthy', data: response };
    } catch (error) {
      return { status: 'error', message: error.message };
    }
  }

  async testMAPI() {
    console.log('🧪 MAPI Test via PROXY...');
    try {
      const geoResult = await this.searchPlaces('Moscow', { size: 1 });
      const priceResult = await this.getPrice(55.7558, 37.6176, 9);
      console.log('✅ Geocoding and Pricing APIs are working via PROXY.');
      return { geocoding: geoResult, pricing: priceResult };
    } catch (error) {
      console.error('❌ MAPI test via PROXY failed', error);
      return { error: error.message };
    }
  }

  // ✅ НОВАЯ ФУНКЦИЯ: Поиск с автоматическим получением границ
  async searchWithBoundaries(query, options = {}) {
    try {
      const places = await this.searchPlaces(query, { ...options, include_boundaries: true });
      
      // Для мест без границ пытаемся получить их отдельно
      const placesWithBoundaries = await Promise.all(
        places.map(async (place) => {
          if (!place.boundaries && place.id && place.type !== 'point') {
            try {
              const boundaries = await this.getPlaceBoundaries(place.id);
              if (boundaries) {
                place.boundaries = boundaries;
              }
            } catch (error) {
              console.warn(`Could not get boundaries for ${place.name}:`, error);
            }
          }
          return place;
        })
      );
      
      return placesWithBoundaries;
    } catch (error) {
      console.error('🔍 Search with boundaries error:', error);
      return this.searchPlaces(query, options); // Fallback to regular search
    }
  }

  // ✅ НОВАЯ ФУНКЦИЯ: Поиск по категориям в определенной области
  async searchByCategory(category, bounds) {
    try {
      console.log('🔍 Searching by category via PROXY:', category, 'in bounds:', bounds);
      
      const response = await this.throttledRequest(`${this.endpoints.maps}/search/category`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          category: category,
          bounds: bounds, // { north, south, east, west }
        }),
      });

      console.log('🔍 Category search results via PROXY:', response);
      return response.results || [];
    } catch (error) {
      console.error('❌ Category search via PROXY error:', error);
      
      // Возвращаем mock данные для демонстрации
      return this.generateMockCategoryResults(category, bounds);
    }
  }

  // ✅ НОВАЯ ФУНКЦИЯ: Определение, является ли запрос категориальным
  isCategorySearch(query) {
    const categories = [
      'заправка', 'газ', 'бензин', 'gas station', 'fuel',
      'ресторан', 'кафе', 'еда', 'restaurant', 'cafe', 'food',
      'больница', 'аптека', 'медицина', 'hospital', 'pharmacy', 'medical',
      'школа', 'университет', 'образование', 'school', 'university', 'education',
      'банк', 'банкомат', 'финансы', 'bank', 'atm', 'finance',
      'отель', 'гостиница', 'размещение', 'hotel', 'accommodation',
      'магазин', 'торговый центр', 'покупки', 'shop', 'mall', 'shopping',
      'парк', 'сквер', 'park', 'square',
      'музей', 'театр', 'museum', 'theater',
      'церковь', 'храм', 'church', 'temple'
    ];
    
    return categories.some(cat =>
      query.toLowerCase().includes(cat.toLowerCase())
    );
  }

  // ✅ НОВАЯ ФУНКЦИЯ: Генерация mock данных для категориального поиска
  generateMockCategoryResults(category, bounds) {
    const mockResults = [];
    const count = Math.floor(Math.random() * 8) + 3; // 3-10 результатов
    
    for (let i = 0; i < count; i++) {
      const lat = bounds.south + Math.random() * (bounds.north - bounds.south);
      const lng = bounds.west + Math.random() * (bounds.east - bounds.west);
      
      mockResults.push({
        id: `category_${category}_${i}`,
        name: `${category} ${i + 1}`,
        address: `Mock Address ${i + 1}`,
        category: category,
        latitude: lat,
        longitude: lng,
        confidence: 0.8 + Math.random() * 0.2
      });
    }
    
    console.log('🎭 Generated mock category results:', mockResults);
    return mockResults;
  }
}

export const mapiService = new MapiService();
export default mapiService;